import { api } from '../lib/http.js';

/** Normalize API result from PascalCase/CamelCase */
const normalizeList = (data) => ({
  list: data?.list ?? data?.List ?? [],
  hasMore: data?.hasMore ?? data?.HasMore ?? false,
  page: data?.page ?? data?.Page ?? 1,
});

const pickArray = (data) => {
  const raw = data?.items ?? data?.Items ?? data?.list ?? data?.List;
  return Array.isArray(raw) ? raw : [];
};

/** Normalize search hits from /api/games/search or /api/games/categories/... */
const normalizeSearchItem = (item) => {
  const id = item?.id ?? item?.Id ?? item?.gameId ?? 0;
  const name = (item?.name ?? item?.Name ?? item?.game_name ?? '').toString();
  return {
    id,
    code: item?.code ?? item?.Code ?? item?.game_code ?? '',
    name,
    imageUrl: item?.imageUrl ?? item?.ImageUrl ?? (id ? `${typeof window !== 'undefined' && window.__gameMediaOrigin ? window.__gameMediaOrigin : ''}/images/posters/${id}.jpg` : ''),
    studioId: item?.studioId ?? item?.StudioId ?? item?.provider_id ?? 0,
    studioName: item?.studioName ?? item?.StudioName ?? item?.studio_name ?? null
  };
};

const normalizeSearchResult = (data) => ({
  items: pickArray(data).map(normalizeSearchItem).filter((item) => item.id > 0 && item.name),
  hasMore: !!(data?.hasMore ?? data?.HasMore)
});

const buildCategoriesSearchEndpoint = (endpoint) => {
  if (!endpoint || endpoint.includes('/categories/')) return endpoint;
  const langMatch = endpoint.match(/^\/([a-z]{2})\/api\/games\/search$/i);
  if (langMatch) return `/${langMatch[1]}/api/games/categories/0/search`;
  return '/api/games/categories/0/search';
};

/** Fetch games for a built endpoint (category or studio) */
export async function fetchGames({ endpoint, qs, signal }) {
  const data = await api.get(endpoint, { searchParams: qs, signal }).json();
  return normalizeList(data);
}

/** Header search: GET ?name=&limit= (with categories API fallback) */
export async function searchGames({ endpoint, name, limit = 10, signal }) {
  const query = (name ?? '').trim();
  if (!query) return { items: [], hasMore: false };

  const usesCategories = endpoint.includes('/categories/');
  const primaryParams = usesCategories
    ? { q: query, limit: String(limit), pageSize: String(limit) }
    : { name: query, limit: String(limit) };

  let data;
  try {
    data = await api.get(endpoint, { searchParams: primaryParams, signal }).json();
  } catch (err) {
    if (err?.name === 'AbortError') throw err;
    data = null;
  }

  let result = normalizeSearchResult(data);

  if (!usesCategories && result.items.length === 0) {
    const fallbackEndpoint = buildCategoriesSearchEndpoint(endpoint);
    try {
      const fallbackData = await api.get(fallbackEndpoint, {
        searchParams: { q: query, limit: String(limit), pageSize: String(limit) },
        signal
      }).json();
      const fallbackResult = normalizeSearchResult(fallbackData);
      if (fallbackResult.items.length > 0) result = fallbackResult;
    } catch (err) {
      if (err?.name === 'AbortError') throw err;
    }
  }

  return result;
}